import {useFeatureIsOn} from "@growthbook/growthbook-react";

import type {Result} from "@khanacademy/wonder-blocks-data";
import {Errors} from "@khanacademy/wonder-stuff-core";
import type {AppFeatures} from "@repo/analytics-growthbook/app-features";
import {getPass} from "@repo/data-cookies";
import {useQuery} from "@repo/data-gql";
import {KAError} from "@repo/logging";

import {TEACHER_ACCESS_GROUPS_QUERY} from "#class-package/content/queries/teacher_access_groups_query.ts";
import useProfile from "#data-access/use-profile.ts";

export default function useShowAssignableKhanmigoActivities(): Result<boolean> {
    const profileResult = useProfile();
    const [teacherAccessGroupsResult] = useQuery(TEACHER_ACCESS_GROUPS_QUERY);

    // The feature flag controlling whether the assignable KM activities
    // feature is enabled at all. This is currently serving true for users
    // where kaLocale in "en", "es" and "pt" and will be removed
    // sometime after the assign from explore project is launched.
    const enableAssignableActivities =
        useFeatureIsOn<AppFeatures>("tx-activity-assignments") ||
        getPass("tx-activity-assignments");

    // This it to allow testing with non-district accounts. This will only ever be
    // for internal KA use.
    const enableActivityAssignmentsForNonDistrictTeachers =
        getPass("tx-activity-assignments") ?? false;

    if (
        teacherAccessGroupsResult.status === "loading" ||
        profileResult.status === "loading"
    ) {
        return {status: "loading"};
    }

    if (
        teacherAccessGroupsResult.status !== "success" ||
        profileResult.status !== "success"
    ) {
        return {
            status: "error",
            error: new KAError(
                "Failed to load teacher access or profile data",
                Errors.Internal,
            ),
        };
    }

    // This is checking that the user has the AI Guide feature enabled
    const isAIGuideEnabled =
        !!profileResult.profile &&
        profileResult.profile.get("isAIGuideEnabled");

    const hasAccessToAIGuideDistrictClassroomTeacher =
        teacherAccessGroupsResult.data.user
            ?.hasAccessToAIGuideDistrictClassroomTeacher ?? false;

    const hasClasses = profileResult.profile?.get("hasClasses") ?? false;

    return {
        status: "success",
        data:
            (enableActivityAssignmentsForNonDistrictTeachers && hasClasses) ||
            (!!enableAssignableActivities &&
                isAIGuideEnabled &&
                hasAccessToAIGuideDistrictClassroomTeacher),
    };
}
